/*
    This component displays the image selected
    Input = selectedDevice, Experiment, sample, Image

    It will get the presigned url of the image from S3 and display it on UI

    A capture is a WIDE field: the target well plus a slice of its neighbours,
    the plate edge and the carousel tabs. Symbiomics asked to see the well and
    not the plate, so the image is rendered through a framing box from
    /cpu/well_framing (see backend framing_service).

    The crop is a VIEW: the stored capture is untouched and "Full frame" returns
    to it, so nothing about the original is lost by framing it.
*/

import React, { useEffect, useState } from 'react';
import { getS3Client } from '../utils/awsClients';

const cpuUrl = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/cpu`
  : null;

const ImageDisplay = ({
  selectedDevice,
  selectedExperiment,
  selectedSample,
  selectedSnapshot,
  selectedImage
}) => {

  const [imageUrl, setImageUrl] = useState(null);
  const [framing, setFraming] = useState(null);   // {box,width,height,detected}
  const [fitWell, setFitWell] = useState(true);

  const basePath = selectedImage
    ? (selectedSnapshot
        ? `${selectedDevice.device_bucket}/Experiments/${selectedSnapshot}/${selectedImage}`
        : `${selectedDevice.device_bucket}/Experiments/${selectedExperiment}/${selectedSample}/${selectedImage}`)
    : null;
  // The well folder is whatever holds this capture, so the prefix is derived by
  // dropping the filename rather than rebuilt from the parts -- snapshots and
  // experiments nest differently and only one of them has a sample level.
  const wellPrefix = basePath ? basePath.slice(0, basePath.lastIndexOf('/') + 1) : null;

  const getImageUrl = async () => {
    try {
      const s3 = await getS3Client();

      if (selectedImage) {
        const listParams = {
          Bucket: process.env.REACT_APP_USER_BUCKET,
          Key: basePath,
          Expires: 3600
        };

        const url = await s3.getSignedUrlPromise('getObject', listParams);
        setImageUrl(url);
      }
    } catch (error) {
      console.error("Error generating presigned URLs:", error);
    }
  };

  useEffect(() => {
    getImageUrl();
  }, [selectedDevice, selectedExperiment, selectedSample, selectedSnapshot, selectedImage]);

  // One framing box per well, not per image: the stage returns to the same
  // position each round, so re-detecting for every frame would spend a request
  // per image to arrive at the same answer -- and a box that drifted frame to
  // frame would make a timelapse jitter.
  useEffect(() => {
    let cancelled = false;
    setFraming(null);
    if (!cpuUrl || !wellPrefix) return undefined;
    (async () => {
      try {
        const res = await fetch(`${cpuUrl}/well_framing`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            bucket: process.env.REACT_APP_USER_BUCKET,
            prefix: wellPrefix,
          }),
        });
        if (!res.ok) throw new Error(`well_framing ${res.status}`);
        const data = await res.json();
        if (!cancelled) setFraming(data);
      } catch (e) {
        // Framing is an enhancement; falling back to the full frame is a
        // perfectly good picture, so this never blocks the image.
        console.warn('well framing unavailable, showing full frame:', e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [wellPrefix]);

  const cropped = fitWell && framing && framing.box;
  let frameStyle = null;
  let imgStyle = {
    marginTop: '10px',
    maxWidth: '100%',
    maxHeight: '60vh',
    objectFit: 'contain'
  };

  if (cropped) {
    const [x0, y0, x1, y1] = framing.box;
    const bw = Math.max(1, x1 - x0), bh = Math.max(1, y1 - y0);
    frameStyle = {
      position: 'relative',
      overflow: 'hidden',
      marginTop: '10px',
      width: '100%',
      maxWidth: `min(100%, ${Math.round(60 * (bw / bh))}vh)`,   // cap at 60vh tall
      aspectRatio: `${bw} / ${bh}`,
    };
    // Percentage margins resolve against the CONTAINER WIDTH for both axes, so
    // one scale factor (container width / box width) positions the image on
    // both, and the crop stays correct at any rendered size.
    imgStyle = {
      display: 'block',
      width: `${(framing.width / bw) * 100}%`,
      marginLeft: `${(-x0 / bw) * 100}%`,
      marginTop: `${(-y0 / bw) * 100}%`,
      maxWidth: 'none',
    };
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
      padding: '10px'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 style={{ margin: 0 }}>{selectedImage}</h2>
        {framing && (
          <button
            onClick={() => setFitWell(v => !v)}
            title={framing.detected
              ? 'Toggle between the detected well and the whole capture'
              : 'No well was detected in this capture; the crop is a centred guess'}
            style={{
              cursor: 'pointer', fontSize: '0.75rem', padding: '3px 8px',
              borderRadius: 4, border: '1px solid #888', background: 'transparent',
              color: 'inherit',
            }}
          >
            {fitWell ? 'Full frame' : 'Fit to well'}
            {framing.detected ? '' : ' (approx.)'}
          </button>
        )}
      </div>
      {imageUrl ? (
        cropped ? (
          <div style={frameStyle}>
            <img src={imageUrl} key={imageUrl} alt={selectedImage || 'capture'} style={imgStyle} />
          </div>
        ) : (
          <img src={imageUrl} key={imageUrl} alt={selectedImage || 'capture'} style={imgStyle} />
        )
      ) : (
        <p>Loading image...</p>
      )}
    </div>
  );
};

export default ImageDisplay;
